/**
 * Робота з текстом відповідей: нормалізація, нечітке порівняння,
 * підсвічування розбіжностей.
 *
 * Навіщо нечітке: учень друкує з телефона, польська діакритика на укр/рос
 * розкладці недоступна. `nie mowie` має зараховуватись як `nie mówię`,
 * але з поміткою «зверни увагу на діакритику».
 */

const PL_FOLD: Record<string, string> = {
  ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
  Ą: 'a', Ć: 'c', Ę: 'e', Ł: 'l', Ń: 'n', Ó: 'o', Ś: 's', Ź: 'z', Ż: 'z',
}

/** Прибирає польську діакритику: `mówię` → `mowie`. */
export function fold(s: string): string {
  let out = ''
  for (const ch of s) out += PL_FOLD[ch] ?? ch
  return out
}

/**
 * Канонічна форма для порівняння: нижній регістр, без діакритики,
 * без пунктуації, одинарні пробіли.
 */
export function normalize(s: string): string {
  return fold(s)
    .toLowerCase()
    .replace(/[.,!?;:—–-]/g, ' ')
    .replace(/[„”"«»'']/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Відстань Левенштейна з відсіканням: якщо більша за `max`, повертає `max + 1`. */
export function editDistance(a: string, b: string, max = Infinity): number {
  if (a === b) return 0
  if (Math.abs(a.length - b.length) > max) return max + 1

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  let curr = new Array<number>(b.length + 1)

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i
    let rowMin = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(curr[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost)
      if (curr[j]! < rowMin) rowMin = curr[j]!
    }
    if (rowMin > max) return max + 1
    ;[prev, curr] = [curr, prev]
  }
  return prev[b.length]!
}

/** Скільки одруківок пробачаємо для рядка такої довжини. */
export function tolerance(len: number): number {
  if (len <= 4) return 0
  if (len <= 8) return 1
  if (len <= 16) return 2
  return 3
}

export type MatchQuality = 'exact' | 'diacritics' | 'typo' | 'wrong'

export interface MatchResult {
  quality: MatchQuality
  correct: boolean
  /** Найближчий з очікуваних варіантів — його показуємо як зразок. */
  best: string
  distance: number
  /** Підказка українською, якщо є що сказати. */
  hint?: string
}

/**
 * Порівнює відповідь учня зі списком прийнятних варіантів.
 * Повертає найкращий збіг і його якість.
 */
export function matchAnswer(input: string, accepted: readonly string[]): MatchResult {
  const given = normalize(input)
  let best: MatchResult = {
    quality: 'wrong',
    correct: false,
    best: accepted[0] ?? '',
    distance: Infinity,
  }

  for (const variant of accepted) {
    if (input.trim() === variant.trim()) {
      return { quality: 'exact', correct: true, best: variant, distance: 0 }
    }
    const target = normalize(variant)
    if (given === target) {
      const same = fold(input.trim().toLowerCase()) === input.trim().toLowerCase()
      // Збіг без діакритики: зараховуємо, але нагадуємо про знаки.
      const result: MatchResult = {
        quality: same && fold(variant) !== variant ? 'diacritics' : 'exact',
        correct: true,
        best: variant,
        distance: 0,
      }
      if (result.quality === 'diacritics') {
        result.hint = 'Зараховано, але зверни увагу на польські знаки: ' + variant
      }
      return result
    }
    const limit = tolerance(target.length)
    const d = editDistance(given, target, limit)
    if (d < best.distance) {
      best = {
        quality: d <= limit ? 'typo' : 'wrong',
        correct: d <= limit,
        best: variant,
        distance: d,
      }
      if (best.correct) best.hint = `Майже точно. Правильно: ${variant}`
    }
  }
  return best
}

/**
 * Порівняння за словами — для підсвічування, де саме помилка.
 * Повертає масив слів очікуваної відповіді з позначкою збігу.
 */
export interface WordDiff {
  word: string
  ok: boolean
}

export function diffWords(expected: string, actual: string): WordDiff[] {
  const exp = expected.split(/\s+/).filter(Boolean)
  const act = new Set(normalize(actual).split(' ').filter(Boolean))
  return exp.map((word) => ({ word, ok: act.has(normalize(word)) }))
}

/** Перемішування з детермінованим зерном — щоб тести були відтворювані. */
export function shuffle<T>(items: readonly T[], seed: number): T[] {
  const out = [...items]
  let s = seed >>> 0 || 1
  for (let i = out.length - 1; i > 0; i--) {
    s ^= s << 13; s >>>= 0
    s ^= s >> 17
    s ^= s << 5; s >>>= 0
    const j = s % (i + 1)
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

/** Розбиває фразу на «плитки» для вправи «збери фразу». */
export function tokenize(phrase: string): string[] {
  return phrase.split(/\s+/).filter(Boolean)
}

/**
 * Українське відмінювання після числівника: 1 картка, 2 картки, 5 карток.
 * Дрібниця, але «1 карток» на екрані одразу читається як недороблений застосунок.
 */
export function plural(n: number, one: string, few: string, many: string): string {
  const last = n % 10
  const two = n % 100
  if (two >= 11 && two <= 14) return many
  if (last === 1) return one
  if (last >= 2 && last <= 4) return few
  return many
}
